import { emptyIfInstant } from "./mockMode";

/**
 * Finance module mock data — loans, expenses, FX revaluation snapshots.
 *
 * Numbers are tuned for a mid-sized Bangladeshi distributor:
 *   - revenue ≈ 50L–2Cr/month
 *   - working capital loans of a few crore total
 *   - operating expenses split across rent, salary, utilities, logistics
 *
 * Mirrors the admin-side type shapes that the UI consumes — the real
 * InstantDB queries return rows that fit these structures.
 */

const NOW = new Date("2026-05-09T08:00:00Z").getTime();
const DAY = 24 * 60 * 60 * 1000;

/* ─────────────────────────────────────────────────────────────
 * Loans
 * ───────────────────────────────────────────────────────────── */

export type MockLoanKind =
  | "dealer_credit"
  | "supplier_advance"
  | "staff_advance"
  | "bank_loan"
  | "other";

export type MockLoanStatus = "active" | "paid_off" | "defaulted" | "written_off";

export type MockLoan = {
  _id: string;
  loanNumber: string;
  loanKind: MockLoanKind;
  counterpartyName: string;
  /** Free-form direction hint for the badge: "incoming" = we lent, "outgoing" = we borrowed. */
  direction: "incoming" | "outgoing";
  currency: string;
  principalCents: bigint;
  outstandingPrincipalCents: bigint;
  /** Snapshot rate * 1e8 used for BDT translation. */
  fxRateToBaseE8: bigint;
  amountInBaseCents: bigint;
  outstandingPrincipalInBaseCents: bigint;
  interestRatePercentE4: bigint;
  interestType: "simple" | "compound" | "none";
  scheduleKind: "emi" | "flat" | "bullet";
  tenorPeriods: number;
  issuedDate: number;
  dueDate: number | null;
  status: MockLoanStatus;
  notes: string | null;
};

export const mockLoans: MockLoan[] = emptyIfInstant<MockLoan>([]);

export type MockLoanRepayment = {
  _id: string;
  loanId: string;
  repaymentNumber: string;
  occurredAt: number;
  principalCents: bigint;
  interestCents: bigint;
  totalCents: bigint;
  currency: string;
  paymentMethod:
    | "cash"
    | "bank_transfer"
    | "cheque"
    | "mobile_banking"
    | "card"
    | "points"
    | "credit_note"
    | "other";
};

export const mockLoanRepayments: MockLoanRepayment[] = emptyIfInstant<MockLoanRepayment>([]);

/* ─────────────────────────────────────────────────────────────
 * Schedule helpers
 * Same amortisation math so the UI can preview locally.
 * ───────────────────────────────────────────────────────────── */

export type MockScheduleEntry = {
  index: number;
  dueDate: number;
  principalCents: bigint;
  interestCents: bigint;
  totalCents: bigint;
  remainingPrincipalCents: bigint;
  /** Has this period been paid (any repayment past dueDate)? */
  isPaid: boolean;
};

function periodRateE8(annualPctE4: bigint, periodsPerYear: number): bigint {
  return (annualPctE4 * 100_000_000n) / (1_000_000n * BigInt(periodsPerYear));
}

function addMonths(baseMs: number, n: number): number {
  const d = new Date(baseMs);
  return Date.UTC(
    d.getUTCFullYear(),
    d.getUTCMonth() + n,
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds(),
  );
}

export function generateMockSchedule(loan: MockLoan): MockScheduleEntry[] {
  const principal = loan.principalCents;
  const annual = loan.interestRatePercentE4;
  const periods = loan.tenorPeriods;
  const start = loan.issuedDate;
  const rE8 = periodRateE8(annual, 12);
  const out: MockScheduleEntry[] = [];

  // Guard against division by zero when periods has been zeroed out
  if (periods <= 0) return out;

  if (loan.scheduleKind === "emi" && annual > 0n) {
    let onePlusRpowN_E8 = 100_000_000n;
    for (let k = 0; k < periods; k++) {
      onePlusRpowN_E8 = (onePlusRpowN_E8 * (100_000_000n + rE8)) / 100_000_000n;
    }
    const numerator = (principal * rE8 * onePlusRpowN_E8) / 100_000_000n;
    const denominator = onePlusRpowN_E8 - 100_000_000n;
    const emi = denominator === 0n ? principal / BigInt(periods) : numerator / denominator;
    let remaining = principal;
    for (let i = 0; i < periods; i++) {
      const interest = (remaining * rE8) / 100_000_000n;
      let principalPart = emi - interest;
      if (i === periods - 1) principalPart = remaining;
      if (principalPart > remaining) principalPart = remaining;
      remaining -= principalPart;
      if (remaining < 0n) remaining = 0n;
      const dueDate = addMonths(start, i + 1);
      out.push({
        index: i + 1,
        dueDate,
        principalCents: principalPart,
        interestCents: interest,
        totalCents: principalPart + interest,
        remainingPrincipalCents: remaining,
        isPaid: dueDate < NOW - 5 * DAY,
      });
    }
    return out;
  }

  if (loan.scheduleKind === "bullet") {
    const interestPerPeriod = (principal * rE8) / 100_000_000n;
    for (let i = 0; i < periods; i++) {
      const isLast = i === periods - 1;
      const principalPart = isLast ? principal : 0n;
      const remaining = isLast ? 0n : principal;
      const dueDate = addMonths(start, i + 1);
      out.push({
        index: i + 1,
        dueDate,
        principalCents: principalPart,
        interestCents: interestPerPeriod,
        totalCents: principalPart + interestPerPeriod,
        remainingPrincipalCents: remaining,
        isPaid: dueDate < NOW - 5 * DAY,
      });
    }
    return out;
  }

  // flat
  const basePrincipal = principal / BigInt(periods);
  const remainder = principal - basePrincipal * BigInt(periods);
  const interestPerPeriod = (principal * rE8) / 100_000_000n;
  let remaining = principal;
  for (let i = 0; i < periods; i++) {
    const principalPart = i === periods - 1 ? basePrincipal + remainder : basePrincipal;
    remaining -= principalPart;
    const dueDate = addMonths(start, i + 1);
    out.push({
      index: i + 1,
      dueDate,
      principalCents: principalPart,
      interestCents: interestPerPeriod,
      totalCents: principalPart + interestPerPeriod,
      remainingPrincipalCents: remaining < 0n ? 0n : remaining,
      isPaid: dueDate < NOW - 5 * DAY,
    });
  }
  return out;
}

/* ─────────────────────────────────────────────────────────────
 * Expenses
 * ───────────────────────────────────────────────────────────── */

export type MockExpenseCategory =
  | "salary"
  | "rent"
  | "utilities"
  | "logistics"
  | "marketing"
  | "travel"
  | "repairs"
  | "office_supplies"
  | "professional_fees"
  | "tax_duty"
  | "bank_charges"
  | "other";

export type MockExpenseStatus = "pending" | "approved" | "paid" | "rejected" | "void";

export type MockExpense = {
  _id: string;
  expenseNumber: string;
  category: MockExpenseCategory;
  description: string;
  branchId: string | null;
  branchLabel: string;
  currency: string;
  amountCents: bigint;
  amountInBaseCents: bigint;
  fxRateToBaseE8: bigint;
  paymentMethod:
    | "cash"
    | "bank_transfer"
    | "cheque"
    | "mobile_banking"
    | "card"
    | "points"
    | "credit_note"
    | "other";
  payeeName: string | null;
  occurredAt: number;
  status: MockExpenseStatus;
  notes: string | null;
};

export const mockExpenses: MockExpense[] = emptyIfInstant<MockExpense>([]);

/* ─────────────────────────────────────────────────────────────
 * FX rates (latest snapshot)
 * ───────────────────────────────────────────────────────────── */

export type MockFxRate = {
  _id: string;
  fromCurrency: string;
  toCurrency: string;
  rateE8: bigint;
  effectiveAt: number;
  source: "manual" | "api" | "central_bank";
  notes: string | null;
};

export const mockFxRatesFinance: MockFxRate[] = emptyIfInstant<MockFxRate>([]);

/* ─────────────────────────────────────────────────────────────
 * FX revaluation snapshot
 * ───────────────────────────────────────────────────────────── */

export type MockFxRevalLine = {
  source: "lc" | "loan" | "invoice";
  recordId: string;
  reference: string;
  currency: string;
  remainingForeignCents: bigint;
  snapshotFxRateE8: bigint;
  currentFxRateE8: bigint;
  bookValueBaseCents: bigint;
  marketValueBaseCents: bigint;
  unrealizedDeltaBaseCents: bigint;
};

export const mockFxRevaluation = {
  asOf: NOW,
  totalUnrealizedBaseCents: 0n + 0n + 0n,
  totalGainBaseCents: 0n + 0n + 0n,
  totalLossBaseCents: 0n,
  lines: emptyIfInstant<MockFxRevalLine>([]),
};
